import { Button, Checkbox, H1, Input, TimelineStep } from '@/design-system'
import * as EmailValidator from 'email-validator'
import { isValidCNP } from '@/lib/cnp'
import { isValidPhoneNumber } from 'libphonenumber-js/min'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import VerifyForm from './VerifyForm'
import clsx from 'clsx'

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITEKEY

type RegistrationFormValues = {
  phone_number?: string
  first_name?: string
  last_name?: string
  cnp?: string
  email?: string
  acord_gdpr?: boolean
  custom: unknown
}

const RegistrationForm = ({
  phoneNr,
  completeCallback,
  cancelCallback,
  profileFields,
  embedded = false,
}) => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RegistrationFormValues>({
    mode: 'onTouched',
    defaultValues: { phone_number: phoneNr },
  })
  const [verificationId, setVerificationId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [globalError, setGlobalError] = useState(null)

  const onSubmit = async (formData) => {
    setSubmitting(true)
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(SITE_KEY, { action: 'submit' })
        .then(async (token) => {
          formData['recaptchaResponse'] = token

          const registerRes = await fetch('/api/auth/register', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
          })

          const registerJson: {
            verification_id: string
            errors: Record<string, string>
            error: string
          } = await registerRes.json()

          if (registerJson.errors) {
            setSubmitting(false)
            for (const [field, err] of Object.entries(registerJson.errors)) {
              if (
                field == 'phone_number' ||
                field == 'email' ||
                field == 'cnp'
              ) {
                setError(field, { type: 'custom', message: err })
              } else {
                setError('custom', { type: 'custom', message: err })
              }
            }
          } else {
            if (registerJson.error) {
              setSubmitting(false)
              setGlobalError(registerJson.error)
            } else {
              setSubmitting(false)
              setVerificationId(registerJson.verification_id)
              window.scrollTo(0, 0)
            }
          }
        })
        .catch((error) => {
          console.log('error', error.message)
        })
    })
  }

  if (verificationId) {
    return (
      <VerifyForm
        verificationId={verificationId}
        verifyDoneCallback={completeCallback}
      />
    )
  }

  return (
    <form
      method="post"
      onSubmit={handleSubmit(onSubmit)}
      className={clsx('sm:mt-0', embedded || '-mt-8')}
    >
      {!embedded && <H1>Creează cont</H1>}
      {globalError && (
        <div className="bg-red-100 p-3 text-red-800 rounded-md">
          {globalError}
        </div>
      )}
      <TimelineStep
        step={1}
        title="Identitate"
        className="md:pt-5"
        hideNumbers={embedded}
      >
        <Input
          label="Prenume"
          className={clsx(
            embedded ? 'col-span-12' : 'col-span-12 sm:col-span-6'
          )}
          autoFocus
          required={true}
          error={errors.first_name}
          {...register('first_name', {
            validate: (v) => {
              if (!v) return 'Prenumele incomplet'
            },
          })}
        />

        <Input
          label="Nume"
          className={clsx(
            embedded ? 'col-span-12' : 'col-span-12 sm:col-span-6'
          )}
          required={true}
          error={errors.last_name}
          {...register('last_name', {
            validate: (v) => {
              if (!v) return 'Numele incomplet'
            },
          })}
        />

        {profileFields.cnp && (
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            label="Cod Numeric Personal (CNP)"
            className={clsx(
              embedded ? 'col-span-12' : 'col-span-12 sm:col-span-6'
            )}
            required={profileFields.cnp.required || false}
            error={errors.cnp}
            {...register('cnp', {
              validate: (v) => {
                if (!v) return 'CNP-ul nu este completat'

                if (!isValidCNP(v)) {
                  return 'CNP invalid'
                }
              },
            })}
          />
        )}
      </TimelineStep>
      <TimelineStep step={2} title="Contact" hideNumbers={embedded}>
        <Input
          type="tel"
          label="Telefon mobil"
          placeholder="Va fi folosit pentru autentificarea prin coduri SMS"
          className={clsx(
            embedded ? 'col-span-12' : 'col-span-12 sm:col-span-6'
          )}
          required={true}
          error={errors.phone_number}
          {...register('phone_number', {
            validate: (v) => {
              if (!v) return 'Număr de telefon incomplet'

              const valid = isValidPhoneNumber(v, 'RO')
              if (valid !== true) {
                return 'Număr de telefon invalid'
              }
            },
          })}
        />
        <Input
          type="email"
          label="E-mail"
          placeholder="Folosit pentru comunicarea cu Primăria"
          className={clsx(
            embedded ? 'col-span-12' : 'col-span-12 sm:col-span-6'
          )}
          required={profileFields.email?.required || false}
          error={errors.email}
          {...register('email', {
            validate: (v) => {
              if (!v) {
                return profileFields.email?.required ? 'E-mail incomplet' : null
              }

              const valid = EmailValidator.validate(v)
              if (valid !== true) {
                return 'Adresă email invalidă'
              }
            },
          })}
        />
        <div className="hidden col-span-6 sm:block"></div>
      </TimelineStep>
      <TimelineStep
        step={3}
        title="Acord GDPR"
        last={true}
        hideNumbers={embedded}
      >
        <Checkbox
          className="col-span-12"
          error={errors.acord_gdpr}
          {...register('acord_gdpr', {
            validate: (v) => {
              if (!v) return 'Declarația pe propria răspundere trebuie bifată'
            },
          })}
        >
          Sunt de acord cu{' '}
          <a
            target="_blank"
            href="https://www.primariatm.ro/politica-de-confidentialitate-privind-prelucrarea-datelor-cu-caracter-personal/"
          >
            Politica de confidențialitate a Primăriei Municipiului Timișoara
          </a>{' '}
          privind prelucrarea datelor cu caracter personal
        </Checkbox>
      </TimelineStep>
      <div className="relative flex">
        <div className="hidden w-8 sm:block"></div>
        <div className="flex-1 md:grid md:grid-cols-4 md:gap-2">
          <div></div>
          <div
            className={clsx(
              'flex items-center col-span-3 pt-2 space-x-4 sm:mx-0',
              embedded || 'mx-4'
            )}
          >
            <Button
              primary
              disabled={submitting}
              className="inline-flex items-center"
            >
              {submitting && (
                <svg
                  className="w-5 h-5 mr-2 text-white animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx={12}
                    cy={12}
                    r={10}
                    stroke="currentColor"
                    strokeWidth={4}
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              Creează cont
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault()
                cancelCallback()
              }}
            >
              Renunță
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}

export default RegistrationForm
